// Shared channel credential + config state for the manager.
//
// The per-agent bot tokens (`agentChannelTokens`) and the team alert webhook
// (`alertSlackWebhook`) are written by the refresh/poll path in
// manager-worker.ts and read by the low-level channel senders in
// ./slack.ts and ./telegram.ts. Hoisting that state into a dependency-free
// leaf module lets both the writers (which stay in manager-worker) and the
// senders (extracted into sibling modules) share the same singletons without
// an import cycle — the same pattern as ./../agent-state.ts. See ADR-0011.

// Per-agent channel bot tokens, populated from `channel_configs` during
// /host/refresh and read by every send path. A `const` Map whose contents are
// mutated in place, so importers on both sides share one instance.
export type AgentChannelToken = {
  slack?: string;
  telegram?: string;
  telegramAllowedChats?: string[];
};
export const agentChannelTokens = new Map<string, AgentChannelToken>();

// Test-only: seed the channel-token cache so delivery paths can run against
// a stubbed fetch without a full /host/refresh cycle (ENG-6038 tests). Accepts
// the full token shape so tests can seed telegramAllowedChats too.
export function __setAgentChannelTokensForTest(
  codeName: string,
  tokens: AgentChannelToken,
): void {
  agentChannelTokens.set(codeName, tokens);
}

// Team alert webhook (first agent's team settings win). Reassignable, so it's
// exposed through accessors rather than a live binding — an ESM consumer can't
// reassign an imported `let`, and the writer (refresh) and a reader
// (sendSlackWebhookMessage) live in different modules.
let alertSlackWebhook: string | null = null;

export function getAlertSlackWebhook(): string | null {
  return alertSlackWebhook;
}

export function setAlertSlackWebhook(value: string | null): void {
  alertSlackWebhook = value;
}
